import type { ReactNode } from 'react'

import { RADIUS } from '@/lib/surface'
import { cn } from '@/lib/utils'

/**
 * A light that travels round a card's FRAME — the montage tiers.
 *
 * Only the frame. Owner's rule for this repo: loudness goes on the border,
 * where there is no text, so nothing here changes the contrast of the copy
 * inside the card. The ring is a 1.5px conic band masked to the edge; the halo
 * under it is the same band blurred, sitting BEHIND the card (`isolate` keeps
 * its negative z-index inside this box instead of under the page).
 *
 * `neutral` is the first rung: a grey light instead of the brand one, so the
 * ladder escalates — grey, then colour. `phase` offsets the loop in seconds, so
 * three frames side by side pass the light along rather than flashing in step.
 *
 * The animation and its reduced-motion stop live in `globals.css`
 * (`.flou-orbit-*`), because a keyframe on a registered property is not
 * something a utility class can say.
 */
export function GlowFrame({
  children,
  neutral = false,
  phase = 0,
  className,
}: {
  children: ReactNode
  neutral?: boolean
  /** Seconds into the loop this frame starts at. */
  phase?: number
  className?: string
}) {
  const offset = { animationDelay: `${(-phase).toFixed(2)}s` }
  const tone = neutral ? 'flou-orbit-neutral' : undefined
  return (
    <div className={cn('relative isolate h-full', RADIUS.feature, className)}>
      <span aria-hidden="true" className={cn('flou-orbit-halo', tone)} style={offset} />
      {children}
      <span aria-hidden="true" className={cn('flou-orbit-ring', tone)} style={offset} />
    </div>
  )
}
